import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react"
import { useSelector } from "react-redux"
import UserService from "services/user.service"
import { Table } from "common/Table/Table"
import { exportToCSV, formatDate } from "utils/funcs"
import swal from "sweetalert"
import { useNotifications } from "hooks/useNotification"
import { useRouter } from "hooks/useRouter"
import { Modal } from "common/Modal"
import { userSelector } from "appRedux/selectors/user"
import { hasPermission } from "utils/permissions"
import classNames from "classnames"
import { Operations } from "./Operations"
import { OperationsByMonth, TravelByMonth } from "./GroupByMonth"
import { ReceiptTransactions } from "./ReceiptTransactions"
import { AddTripModal } from "components/AddTripModal"
import { EvoFiltersBar } from "components/evo/EvoFiltersBar"
import { CompletionStatusControl } from "components/evo/CompletionStatusControl"
import {
  EvoButton,
  EvoFieldLabel,
  EvoInput,
  EvoPageHeader,
  EvoSelect,
  EvoStatTile,
} from "components/evo"
import { usePersistedFilters } from "hooks/usePersistedFilters"
import {
  buildEvoQueryParams,
  formatAmountSubmitted,
  formatMoney,
  getPersistedTripsTab,
  myTripsPathForTab,
  reviewedFromSubmission,
  setPersistedTripsTab,
  submissionDateDisplay,
  submissionFromReviewed,
  tabFromSlug,
  tabToSlug,
  toDateInputValue,
} from "utils/evoTripsUi"

const service = new UserService()

export const MyTrips = () => {
  const { push, replace, query } = useRouter()
  const [myTrips, setMyTrips] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState(() => {
    const fromUrl = tabFromSlug(String(query.tab ?? ""))
    if (fromUrl) return fromUrl
    return getPersistedTripsTab()
  })
  const [totalItems, setTotalItems] = useState(0)
  const { successMessage } = useNotifications()
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 100,
  })
  const [showAddTripModal, setShowAddTripModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedTrip, setSelectedTrip] = useState<any>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)
  const [editFormData, setEditFormData] = useState({
    reviewed: "No",
    submissionDate: "",
    approvedReimbursements: "",
  })
  const { filters, setFilters, clearFilters } = usePersistedFilters("my-trips-list")
  const currentUser = useSelector(userSelector)

  const {
    canViewTripDetails,
    canEditTrips,
    canDeleteTrips,
  } = useMemo(() => {
    const viewTrips = hasPermission(currentUser, "myTrips")
    return {
      canViewTripDetails:
        hasPermission(currentUser, "tripDetails") || viewTrips,
      canEditTrips: hasPermission(currentUser, "myTrips", "edit"),
      canDeleteTrips: hasPermission(currentUser, "myTrips", "delete"),
    }
  }, [currentUser])

  const handleChangeStatus = (id: string, status: "Paid" | "Not-Paid") => {
    setUpdatingStatusId(id)
    service
      .updateMyTrip(id, { tripStatus: status })
      .then((resp) => {
        if (resp.data.success) {
          successMessage("Trip status updated successfully")
          setMyTrips((prev) =>
            prev.map((trip) =>
              String(trip._id) === String(id) ? { ...trip, tripStatus: status } : trip
            )
          )
        }
      })
      .catch((error) => {
        console.log(error)
        swal("Error", "Failed to update trip status. Please try again.", "error")
      })
      .finally(() => {
        setUpdatingStatusId(null)
      })
  }

  const handleEditClick = (trip: any) => {
    if (!canEditTrips) return
    setSelectedTrip(trip)
    setEditFormData({
      reviewed: reviewedFromSubmission(trip.expenseSubmissionOpenOrClose),
      submissionDate: toDateInputValue(trip.submissionDate),
      approvedReimbursements: trip.approvedReimbursements
        ? String(trip.approvedReimbursements)
        : "",
    })
    setShowEditModal(true)
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTrip || isUpdating || !canEditTrips) return

    const updateData: Record<string, unknown> = {
      reviewed: editFormData.reviewed,
      expenseSubmissionOpenOrClose: submissionFromReviewed(editFormData.reviewed),
    }
    if (editFormData.submissionDate) {
      updateData.submissionDate = editFormData.submissionDate
    }
    if (editFormData.approvedReimbursements) {
      updateData.approvedReimbursements =
        parseFloat(editFormData.approvedReimbursements) || 0
    }

    setIsUpdating(true)
    service
      .updateMyTrip(selectedTrip._id, updateData)
      .then((resp) => {
        if (resp.data.success) {
          successMessage("Trip updated successfully")
          setMyTrips((prev) =>
            prev.map((trip) =>
              trip._id === selectedTrip._id ? { ...trip, ...updateData } : trip
            )
          )
          setShowEditModal(false)
          setSelectedTrip(null)
          setEditFormData({ reviewed: "No", submissionDate: "", approvedReimbursements: "" })
        }
      })
      .catch((error) => {
        console.log(error)
        swal("Error", "Failed to update trip. Please try again.", "error")
      })
      .finally(() => {
        setIsUpdating(false)
      })
  }

  const handleDelete = (id: string) => {
    if (!canDeleteTrips) return
    swal({
      title: "Are you sure you want to delete this trip?",
      text: "This action cannot be undone.",
      icon: "warning",
      className: "w-[500px]",
      buttons: {
        cancel: { text: "Cancel", visible: true },
        confirm: { text: "Delete", className: "bg-red-600 text-white" },
      },
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        service
          .deleteMyTrip(id)
          .then((resp) => {
            if (resp.data.success) {
              successMessage("Trip deleted successfully")
              setMyTrips((prev) => prev.filter((trip) => trip._id !== id))
              setTotalItems((n) => n - 1)
            }
          })
          .catch((error) => {
            console.log(error)
            swal("Error", "Failed to delete trip. Please try again.", "error")
          })
      }
    })
  }

  const columns = useMemo(
    () => [
      { key: "badgeId", dbColName: "badgeId", title: "Badge ID", freeze: true, freezeIndex: "left-0" },
      {
        key: "fullName",
        dbColName: "fullName",
        title: "Full Name",
        isSort: true,
      },
      { key: "name", dbColName: "name", title: "Trip Name" },
      {
        key: "startDate",
        dbColName: "startDate",
        title: "Start Date",
        render: (date: string) => formatDate(date),
      },
      {
        key: "endDate",
        dbColName: "endDate",
        title: "End Date",
        render: (date: string) => formatDate(date),
      },
      {
        key: "submissionDate",
        dbColName: "submissionDate",
        title: "Submission Date",
        render: (date: string) => submissionDateDisplay(date),
      },
      {
        key: "amountSubmitted",
        title: "Amount Submitted",
        render: (trip: any) => {
          const { text, incomplete } = formatAmountSubmitted(trip.approvedReimbursements)
          return (
            <span className={incomplete ? "text-red-600 font-medium" : ""}>{text}</span>
          )
        },
      },
      {
        key: "approvedAmountToPay",
        dbColName: "totalToBePaid",
        title: "Approved Amount to Pay",
        render: (value: number) => formatMoney(value),
      },
      {
        key: "actions",
        title: "Actions",
        freeze: true,
        freezeIndex: "right-0",
        render: (trip: any) => {
          const buttons: ReactElement[] = []

          if (canDeleteTrips) {
            buttons.push(
              <button
                key="delete"
                type="button"
                onClick={() => handleDelete(trip._id)}
                className="rounded p-1 text-red-600 transition hover:bg-red-50 hover:text-red-800"
                title="Delete trip"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </button>
            )
          }

          if (canViewTripDetails) {
            buttons.push(
              <button
                key="view"
                type="button"
                onClick={() => {
                  setPersistedTripsTab("My Trips")
                  push(`/trip-details/${trip._id}`)
                }}
                className="rounded p-1 text-blue-600 transition hover:bg-blue-50 hover:text-blue-800"
                title="View trip"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              </button>
            )
          }

          if (canEditTrips) {
            buttons.push(
              <button
                key="edit"
                type="button"
                onClick={() => handleEditClick(trip)}
                className="rounded p-1 text-blue-600 transition hover:bg-blue-50 hover:text-blue-800"
                title="Edit trip"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-6 w-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                </svg>
              </button>
            )
          }

          if (!buttons.length) {
            return <span className="text-xs text-gray-400">No actions</span>
          }

          return (
            <div className="flex items-center gap-2">
              {canEditTrips && (
                <CompletionStatusControl
                  rowId={String(trip._id)}
                  status={trip?.tripStatus}
                  disabled={updatingStatusId === trip._id}
                  onChange={(status) => handleChangeStatus(trip._id, status)}
                />
              )}
              {buttons}
            </div>
          )
        },
      },
    ],
    [canDeleteTrips, canEditTrips, canViewTripDetails, push, updatingStatusId]
  )

  useEffect(() => {
    setPersistedTripsTab(tab)
    const slug = tabToSlug(tab)
    if (String(query.tab ?? "").toLowerCase() !== slug) {
      replace(myTripsPathForTab(tab))
    }
  }, [tab, query.tab, replace])

  useEffect(() => {
    const fromUrl = tabFromSlug(String(query.tab ?? ""))
    if (fromUrl && fromUrl !== tab) {
      setTab(fromUrl)
      setPersistedTripsTab(fromUrl)
    }
  }, [query.tab, tab])

  const selectTab = (label: string) => {
    setPersistedTripsTab(label)
    setTab(label)
    replace(myTripsPathForTab(label))
  }

  const fetchData = useCallback(() => {
    setLoading(true)
    service
      .getMyTrips(buildEvoQueryParams(pagination, filters))
      .then((resp) => {
        setMyTrips(resp.data.data || [])
        setTotalItems(resp.data.totalDocument || resp.data.data?.length || 0)
      })
      .catch((err) => {
        console.log(err)
        setMyTrips([])
        setTotalItems(0)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [pagination, filters.fullName, filters.month, filters.year])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    setPagination((p) => ({ ...p, pageNumber: 1 }))
  }, [filters.fullName, filters.month, filters.year])

  const handleExport = async () => {
    setExporting(true)
    try {
      const resp = await service.getMyTrips({
        ...buildEvoQueryParams(pagination, filters),
        isDownload: "true",
      })
      exportToCSV(resp.data.data ?? [], "my-trips")
    } catch (e) {
      console.error(e)
    } finally {
      setExporting(false)
    }
  }

  const TABS = [
    { label: "My Trips", icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
      </svg>
    )},
    { label: "Travel by Month", icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
      </svg>
    )},
    { label: "Operations", icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z" />
      </svg>
    )},
    { label: "Operations by Month", icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
      </svg>
    )},
    { label: "Receipt Transactions", icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 14.25l6-6m4.5-3.493V21.75l-3.75-1.5-3.75 1.5-3.75-1.5-3.75 1.5V4.757c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0c1.1.128 1.907 1.077 1.907 2.185Z" />
      </svg>
    )},
  ]

  const tabSubtitle: Record<string, string> = {
    "My Trips": "Trips, reimbursements, and submission status.",
    "Travel by Month": "Travel expenses grouped by month with expandable receipts.",
    Operations: "Monthly operation submissions and approvals.",
    "Operations by Month": "Operations grouped by month with payment filters.",
    "Receipt Transactions": "All receipt line items across trips and operations.",
  }

  return (
    <Fragment>
      <EvoPageHeader
        title="Travel & operations"
        subtitle={tabSubtitle[tab] ?? "Manage trips, operations, and receipts."}
        actions={
          tab === "My Trips" ? (
            <EvoButton type="button" onClick={() => setShowAddTripModal(true)}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
              Create trip
            </EvoButton>
          ) : undefined
        }
      />

      <div className="mb-5 overflow-hidden rounded-xl border border-evo-border bg-[var(--evo-surface)] shadow-[var(--evo-shadow-sm)]">
        <div className="flex items-center overflow-x-auto scrollbar-hide px-2">
          {TABS.map(({ label, icon }) => {
            const isActive = tab === label
            return (
              <button
                key={label}
                type="button"
                onClick={() => selectTab(label)}
                className={classNames(
                  "relative flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
                  isActive ? "text-primary" : "text-evo-muted hover:text-[var(--evo-text)]"
                )}
              >
                <span className={classNames("transition-colors duration-150", isActive ? "text-primary" : "text-evo-subtle")}>
                  {icon}
                </span>
                {label}
                <span
                  className={classNames(
                    "absolute bottom-0 left-0 right-0 h-0.5 rounded-full transition-all duration-200",
                    isActive ? "bg-primary opacity-100" : "opacity-0"
                  )}
                />
              </button>
            )
          })}
        </div>
      </div>

      {tab === "My Trips" && (
        <>
        <div className="mb-4 grid max-w-xs grid-cols-1 gap-3 sm:grid-cols-2">
          <EvoStatTile
            label="Trips"
            value={loading ? "—" : totalItems}
            hint="Matches current filters"
          />
        </div>
        <Table
          data={myTrips}
          loadingData={loading}
          setData={setMyTrips}
          totalItems={totalItems}
          columns={columns}
          pagination={pagination}
          setPagination={setPagination}
          exporting={exporting}
          handleExport={handleExport}
          showSearch={false}
          filters={
            <EvoFiltersBar
              filters={filters}
              onChange={(patch) => setFilters(patch)}
              onClear={clearFilters}
            />
          }
        />
        </>
      )}
      {tab === "Travel by Month" && <TravelByMonth />}
      {tab === "Operations" && <Operations />}
      {tab === "Operations by Month" && <OperationsByMonth />}
      {tab === "Receipt Transactions" && <ReceiptTransactions />}

      <AddTripModal
        showModal={showAddTripModal}
        setShowModal={setShowAddTripModal}
        onSuccess={fetchData}
      />

      <Modal
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        title="Edit Trip"
        onClose={() => {
          setSelectedTrip(null)
          setIsUpdating(false)
          setEditFormData({ reviewed: "No", submissionDate: "", approvedReimbursements: "" })
        }}
        width="w-full md:w-1/3"
      >
        <form onSubmit={handleEditSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-1.5">
            <EvoFieldLabel>Reviewed</EvoFieldLabel>
            <EvoSelect
              value={editFormData.reviewed}
              onChange={(e) => setEditFormData({ ...editFormData, reviewed: e.target.value })}
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </EvoSelect>
          </div>

          <div className="flex flex-col gap-1.5">
            <EvoFieldLabel>Date submitted</EvoFieldLabel>
            <EvoInput
              type="date"
              value={editFormData.submissionDate}
              onChange={(e) => setEditFormData({ ...editFormData, submissionDate: e.target.value })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <EvoFieldLabel>Amount submitted</EvoFieldLabel>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-evo-subtle">$</span>
              <EvoInput
                type="number"
                step="0.01"
                min="0"
                value={editFormData.approvedReimbursements}
                onChange={(e) => setEditFormData({ ...editFormData, approvedReimbursements: e.target.value })}
                placeholder="0.00"
                className="pl-7"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2.5 border-t border-evo-border pt-2">
            <EvoButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowEditModal(false)
                setSelectedTrip(null)
                setEditFormData({ reviewed: "No", submissionDate: "", approvedReimbursements: "" })
              }}
            >
              Cancel
            </EvoButton>
            <EvoButton type="submit" loading={isUpdating} disabled={isUpdating}>
              {isUpdating ? "Updating…" : "Save changes"}
            </EvoButton>
          </div>
        </form>
      </Modal>
    </Fragment>
  )
}
